import type { DomainMessagesTypes } from 'shared/domain/messages/message';

/**
 * Contract of one channel, shared by the page and the service worker:
 * `requests` the worker answers (method -> request and response payloads) and
 * `events` it broadcasts (message type -> payload).
 */
export interface ChannelContractShape {
  requests: Record<string, { request: unknown; response: unknown }>;
  events: Partial<Record<DomainMessagesTypes, unknown>>;
}

export interface ChannelContract<TShape extends ChannelContractShape> {
  /** Unique per contract: routes its messages on the shared channel bus. */
  readonly key: string;
  /** Type-level only: carries the shape, never set at runtime. */
  readonly shape?: TShape;
}

export function defineChannelContract<TShape extends ChannelContractShape>(key: string): ChannelContract<TShape> {
  return { key };
}

/** Any contract, for code that only routes its messages (bus, logs). */
export type AnyChannelContract = ChannelContract<ChannelContractShape>;

export type ContractShapeOf<TContract> = TContract extends ChannelContract<infer TShape> ? TShape : never;

export type RequestMethod<TShape extends ChannelContractShape> = keyof TShape['requests'] & string;

export type RequestPayload<
  TShape extends ChannelContractShape,
  TMethod extends RequestMethod<TShape>,
> = TShape['requests'][TMethod]['request'];

export type ResponsePayload<
  TShape extends ChannelContractShape,
  TMethod extends RequestMethod<TShape>,
> = TShape['requests'][TMethod]['response'];

export type EventType<TShape extends ChannelContractShape> = keyof TShape['events'] & DomainMessagesTypes;

/** Fields an event carries besides its payload. */
export interface ContractEventMeta {
  /** Correlates the outcome with the command that caused it. */
  uniqueId?: string;
  error?: unknown;
  /** Translation key of the error shown to the user. */
  messageId?: string;
}

/** One event of the contract; a `switch (event.type)` narrows `data`. */
export type ContractEvent<TShape extends ChannelContractShape> = {
  [TType in EventType<TShape>]: ContractEventMeta & {
    type: TType;
    data?: TShape['events'][TType];
  };
}[EventType<TShape>];

/** Payload of one event type of the contract. */
export type EventPayload<
  TShape extends ChannelContractShape,
  TType extends EventType<TShape>,
> = TShape['events'][TType];

export type ContractEventOfType<
  TShape extends ChannelContractShape,
  TType extends EventType<TShape>,
> = Extract<ContractEvent<TShape>, { type: TType }>;

/** One event type of a contract, e.g. `ContractEventOf<typeof levelContract, DomainMessagesTypes.updatedEntity>`. */
export type ContractEventOf<
  TContract extends ChannelContract<ChannelContractShape>,
  TType extends EventType<ContractShapeOf<TContract>>,
> = ContractEventOfType<ContractShapeOf<TContract>, TType>;
